import { useEffect, useRef, useState } from "react";
import { Bot, Send, Loader2, User, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/api";
import api from "@/lib/api";

const SESSION_ID = "primary";

const PROMPTS = [
  "Give me a prioritized fix plan for my critical findings",
  "Generate a Terraform patch for the public S3 bucket",
  "Draft a SOC 2 CC6.1 access control policy",
  "What's my biggest security risk right now and why?",
];

export default function Copilot() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get(`/ai/history/${SESSION_ID}`).then(({ data }) => {
      setMessages(data.map((m) => ({ role: m.role, content: m.content })));
    });
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("aicc_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ session_id: SESSION_ID, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection error. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <div className="mb-4">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink flex items-center gap-2.5">
          <Bot className="text-cyber" size={28} /> AI Security Engineer
        </h1>
        <p className="text-sm text-muted mt-1">Your autonomous copilot — aware of your live findings and compliance posture.</p>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto card-surface rounded-2xl p-4 sm:p-6 space-y-5">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center py-10">
            <div className="w-14 h-14 rounded-2xl bg-cyber/15 flex items-center justify-center mb-5">
              <Sparkles className="text-cyber" size={26} />
            </div>
            <p className="text-ink font-display font-semibold text-lg">How can I help secure your stack?</p>
            <p className="text-sm text-muted mt-1 max-w-sm">Ask for a fix plan, a code/config patch, or a compliance policy draft.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-2.5 max-w-xl w-full">
              {PROMPTS.map((p, i) => (
                <button key={i} onClick={() => send(p)} data-testid={`prompt-${i}`}
                  className="text-left text-sm text-muted hover:text-ink border border-line rounded-xl px-4 py-3 hover:border-cyber transition-colors">
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${m.role}-${i}`}>
            {m.role === "assistant" && (
              <div className="w-8 h-8 rounded-lg bg-cyber/15 flex items-center justify-center shrink-0">
                <Bot size={16} className="text-cyber" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user" ? "bg-cyber text-[#0B0E14] font-medium" : "bg-[var(--surface-2)] text-ink border border-line"}`}>
              {m.content || (streaming && i === messages.length - 1 ? <Loader2 size={15} className="animate-spin" /> : "")}
            </div>
            {m.role === "user" && (
              <div className="w-8 h-8 rounded-lg bg-[var(--surface-2)] border border-line flex items-center justify-center shrink-0">
                <User size={16} className="text-muted" />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-end gap-2.5">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask your AI Security Engineer…"
          data-testid="copilot-input"
          className="resize-none bg-[var(--surface)] border-line text-ink min-h-[52px] max-h-32"
          rows={1}
        />
        <Button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="copilot-send"
          className="bg-cyber text-[#0B0E14] hover:bg-cyber-dim h-[52px] px-5">
          {streaming ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </Button>
      </div>
    </div>
  );
}
